import { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, Check, X, Loader2, Wallet } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

export default function AdminDisputes() {
  const { token } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [active, setActive] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [note, setNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/disputes?status=${filter}`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) setDisputes(await r.json());
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [token, filter]);

  useEffect(() => { fetchDisputes(); }, [fetchDisputes]);

  const openResolve = (d) => { setActive(d); setRefundAmount(String(d.order_total || '')); setNote(''); };

  const resolve = async (action) => {
    setResolving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/disputes/${active.dispute_id}/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action, amount: parseFloat(refundAmount) || 0, note })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      setActive(null);
      fetchDisputes();
    } catch (e) { toast.error(e.message); }
    finally { setResolving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-disputes">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-primary" /> Disputes &amp; Refunds
          </h1>
          <p className="text-slate-500 mt-1">Review customer issues and process wallet refunds</p>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {['open', 'resolved', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize ${filter === f ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
              data-testid={`dispute-filter-${f}`}>{f}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-24" /></Card>)}</div>
      ) : disputes.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">No {filter !== 'all' ? filter : ''} disputes</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {disputes.map(d => (
            <Card key={d.dispute_id} data-testid={`dispute-${d.dispute_id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-slate-900">{d.customer_name || 'Customer'}</span>
                    <Badge variant="outline" className="text-xs">#{d.order_id.slice(-8).toUpperCase()}</Badge>
                    <Badge className={d.status === 'open' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}>
                      {d.status === 'open' ? 'Open' : (d.resolution || 'Resolved')}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600">{d.reason}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Order total R{(d.order_total || 0).toFixed(2)} · {new Date(d.created_at).toLocaleString('en-ZA')}
                    {d.refunded_amount > 0 && ` · Refunded R${d.refunded_amount.toFixed(2)}`}
                  </p>
                </div>
                {d.status === 'open' && (
                  <Button size="sm" onClick={() => openResolve(d)} data-testid={`resolve-${d.dispute_id}`}>Resolve</Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Resolve dispute</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium">{active.customer_name}</p>
                <p className="text-slate-600 mt-1">{active.reason}</p>
                <p className="text-xs text-slate-400 mt-1">Order total: R{(active.order_total || 0).toFixed(2)}</p>
              </div>
              <div>
                <label className="text-sm font-medium">Refund amount (R) — credited to wallet</label>
                <Input type="number" min="0" max={active.order_total} value={refundAmount}
                  onChange={e => setRefundAmount(e.target.value)} data-testid="dispute-refund-amount" />
              </div>
              <div>
                <label className="text-sm font-medium">Internal note (optional)</label>
                <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Reason / resolution detail" />
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => resolve('reject')} disabled={resolving} data-testid="dispute-reject">
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button onClick={() => resolve('refund_wallet')} disabled={resolving} className="bg-primary" data-testid="dispute-refund">
                  {resolving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                  Refund to wallet
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
